import { FinanceDecisionDialog } from '@/components/teacher/FinanceDecisionDialog'
import { useEffect, useMemo, useState } from 'react'
import { useTeacherBookings } from '@/hooks/useTeacherBookings'
import type { TeacherBooking } from '@/services/api/teacher'
import { financeUiEnabled } from '@/utils/financeUi'

function formatDateLabel(dateStr: string): string {
  try {
    const d = new Date(`${dateStr}T00:00:00`)
    if (Number.isNaN(d.getTime())) return dateStr
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000)
    if (diffDays === 0) return 'Heute'
    if (diffDays === 1) return 'Morgen'
    return d.toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: 'long' })
  } catch {
    return dateStr
  }
}

const PAYER_LABELS: Record<string, string> = {
  school: 'Schule zahlt',
  private: 'Privat zahlt',
}

const PAYOUT_LABELS: Record<string, string> = {
  lesson_account: 'IPB',
  paid_out: 'Auszahlung',
}

function BookingRow({ booking, onRequestFinance }: { booking: TeacherBooking; onRequestFinance?: () => void }) {
  const time = booking.start_time
    ? `${booking.start_time.slice(0, 5)}${booking.end_time ? `–${booking.end_time.slice(0, 5)}` : ''}`
    : 'Zeit unbekannt'
  const title = `${booking.subject_name} ${booking.klass_name}`.trim()
  const metaParts: string[] = []
  if (booking.requester_name) metaParts.push(`Bei ${booking.requester_name}`)
  if (booking.room_name) metaParts.push(booking.room_name)
  const meta = metaParts.join(' · ')
  const payoutLabel = booking.substitute_payout
    ? (PAYOUT_LABELS[booking.substitute_payout] || booking.substitute_payout)
    : 'Wahl'
  const showFinance = financeUiEnabled(booking.hr_module_enabled)
    && Boolean(onRequestFinance || booking.substitute_payout)

  return (
    <div className="flex items-center gap-4 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
      <div className="w-24 text-sm text-gray-600 flex-shrink-0">{time}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{title || 'Vertretung'}</div>
        {meta && <div className="text-xs text-gray-500 truncate">{meta}</div>}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0" data-testid="booking-finance-line">
        {booking.director_enabled === true && booking.payment_type && (
          <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
            booking.payment_type === 'school'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-gray-100 text-gray-700'
          }`}>
            {PAYER_LABELS[booking.payment_type] || booking.payment_type}
          </span>
        )}
        {showFinance && onRequestFinance && (
          <button
            type="button"
            onClick={onRequestFinance}
            className="px-1.5 py-0.5 text-xs rounded font-medium border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
            title="Finanz-Wahl ändern: IPB oder Auszahlung"
          >
            {payoutLabel}
          </button>
        )}
        {showFinance && !onRequestFinance && (
          <span className="px-1.5 py-0.5 text-xs rounded font-medium bg-indigo-50 text-indigo-800">
            {payoutLabel}
          </span>
        )}
      </div>
    </div>
  )
}

export function TeacherBookingsPage() {
  const { bookings, isLoading, error, fetchBookings } = useTeacherBookings(100)
  const [refreshKey, setRefreshKey] = useState(0)
  const [financeRequestId, setFinanceRequestId] = useState<string | null>(null)

  useEffect(() => {
    fetchBookings()
  }, [fetchBookings, refreshKey])

  const grouped = useMemo(() => {
    const groups: { key: string; label: string; items: TeacherBooking[] }[] = []
    for (const booking of bookings) {
      const key = booking.date || 'unknown'
      const label = booking.date ? formatDateLabel(booking.date) : 'Datum unbekannt'
      let group = groups.find(g => g.key === key)
      if (!group) {
        group = { key, label, items: [] }
        groups.push(group)
      }
      group.items.push(booking)
    }
    return groups
  }, [bookings])

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Meine Vertretungen</h1>
          <p className="text-sm text-gray-500">
            {bookings.length > 0 ? `${bookings.length} kommende Einsätze` : 'Kommende Einsätze als Stellvertretung'}
          </p>
        </div>
        <button
          onClick={() => setRefreshKey(k => k + 1)}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Aktualisieren"
          title="Aktualisieren"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </button>
      </div>

      {error && (
        <div className="mb-4">
          <div className="text-sm text-red-600 p-3 bg-red-50 rounded-lg">{error}</div>
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3" aria-busy="true">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="p-3 rounded-lg border border-gray-100">
              <div className="h-4 bg-gray-100 rounded animate-pulse w-1/3" />
              <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2 mt-2" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && !error && bookings.length === 0 && (
        <div className="text-sm text-gray-500 p-8 text-center bg-white rounded-lg border border-gray-100">
          Keine kommenden Vertretungen
        </div>
      )}

      {!isLoading && grouped.length > 0 && (
        <div className="space-y-6">
          {grouped.map(group => (
            <section key={group.key} aria-label={group.label}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {group.label}
              </h2>
              <div className="space-y-2">
                {group.items.map(booking => (
                  <BookingRow
                    key={booking.id}
                    booking={booking}
                    onRequestFinance={
                      financeUiEnabled(booking.hr_module_enabled)
                        && booking.can_change_finance && booking.request_id
                        ? () => setFinanceRequestId(booking.request_id ?? null)
                        : undefined
                    }
                  />
                ))}
              </div>
            </section>
          ))}
          {bookings.length >= 100 && (
            <p className="text-xs text-gray-400 text-center">
              Es werden die nächsten 100 Einsätze angezeigt
            </p>
          )}
        </div>
      )}

      <FinanceDecisionDialog
        isOpen={financeRequestId !== null}
        kind="substitute_payout"
        requestId={financeRequestId ?? ''}
        currentValue={
          bookings.find(b => b.request_id === financeRequestId)?.finance_payout
          ?? bookings.find(b => b.request_id === financeRequestId)?.substitute_payout
          ?? null
        }
        personalValue={
          bookings.find(b => b.request_id === financeRequestId)?.finance_payout ?? null
        }
        schoolDefault={
          bookings.find(b => b.request_id === financeRequestId)?.finance_school_default ?? 'lesson_account'
        }
        onClose={() => setFinanceRequestId(null)}
        onSuccess={() => {
          setFinanceRequestId(null)
          setRefreshKey(k => k + 1)
        }}
      />
    </div>
  )
}
